import _, { uniqueId } from 'lodash';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AddPlus2, ErrorWarningtraiangle, Info } from '../../assets/icons';
import { Currency, CURRENCY_IDLE_FEE_RANGES } from '../../constant/Currency.constant';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../constant/Events';
import { IdleFeeStatus } from '../../constant/IdleFeeStatus.constant';
import { useCompany } from '../../hooks/useCompany';
import { useCompanyFeePackage } from '../../hooks/useCompanyFeePackage';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { RoutePath } from '../../routes';
import { useFetchCompanyDetailQuery } from '../../services/company.api';
import { useFetchLocationsQuery } from '../../services/location.api';
import { useLogger } from '../../services/logging/logger-context';
import { useCheckChargerPriceRuleGuardrailsMutation } from '../../services/priceGuardrails.api';
import {
  useCreatePricingModelMutation,
  useEditPricingModelMutation,
  useFetchPricingModelsQuery,
} from '../../services/pricing.api';
import { getCurrentTheme } from '../../stores/selectors/theme.selector';
import { DropdownOption } from '../../stores/types/pm.interface';
import {
  DayPrice,
  EditPricingPayload,
  GenericPricingPayload,
  Price,
  PriceRuleGuardrail,
  RuleTypes,
  ScheduleType,
} from '../../stores/types/price.interface';
import { Charger } from '../../stores/types/sessions.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  CheckBoxData,
  ColorType,
  Dropdown,
  DropdownType,
  FormInput,
  Icon,
  Label,
  LabelType,
  MODAL_TYPES,
  Pill,
  useGlobalModalContext,
} from '../_ui';
import { Form } from '../_ui/Form.component';
import { validateCurrency } from '../_ui/modal/utils';
import type { IdleFeeDetails, SessionTimeLimitDetails } from './PriceForm/PriceForm.component';
import PriceForm from './PriceForm/PriceForm.component';
import { TierPriceItem, tierPriceItem } from './PriceForm/utils';
import { capitalizeFirstLetter, getScheduleLabel, isChargerFromDefaultRule } from './utils';

export const EditPricingRules = () => {
  const { t, i18n } = useTranslation();
  const { logger } = useLogger();
  const { navigateWithCompanyId } = useNavigateWithSearchParam();
  const theme = useSelector(getCurrentTheme);
  const { companyId, userScope } = useCompany();
  const { sessionFee } = useCompanyFeePackage();
  const { showModal } = useGlobalModalContext();
  const [showDefaultChargeDropdown, setShowDefaultChargerDropdown] = useState(false);
  const [guardrailAlertMessages, setGuardrailAlertMessages] = useState<string[]>([]);
  const [dismissedGuardrails, setDismissedGuardrails] = useState<Set<string>>(new Set());
  const { data: company } = useFetchCompanyDetailQuery({
    id: companyId!,
    scope: userScope!,
  });
  const { defaultPricingRule } = useFetchPricingModelsQuery(
    { companyId: companyId!, scope: userScope! },
    {
      selectFromResult: (endPoint) => ({
        defaultPricingRule: endPoint.data
          ? endPoint.data.find((priceRule) => priceRule.defaultPrice)
          : undefined,
      }),
    },
  );

  const currentLocation = useLocation();
  const { state } = currentLocation as { state: { price: Price } };
  const price = state?.price;

  const isSessionLimitEnabled = useFeatureFlagEnabled('2025_session_limit');

  const [name, setName] = useState(price?.displayName || '');
  const [chargers, setChargers] = useState<string[]>([]);

  const isDefaultPricingRule = useMemo(() => {
    return defaultPricingRule && price && price?.id === defaultPricingRule?.id;
  }, [defaultPricingRule, price]);

  useEffect(() => {
    // For default pricing rule, the init chargers array is empty
    if (price?.chargers && !isDefaultPricingRule) {
      setChargers(price.chargers.map((charger) => charger.chargerId));
    }
  }, [price, isDefaultPricingRule, defaultPricingRule]);
  const [dayPrices, setDayPrices] = useState(price?.dayPrices || []);

  const getScheduleType = (type: ScheduleType | RuleTypes) => {
    let priceResult;
    let scheduleResult;
    switch (type) {
      case RuleTypes.FREE:
        priceResult = RuleTypes.FREE;
        break;
      case RuleTypes.TIERED:
        priceResult = RuleTypes.TIERED;
        break;
      default:
        priceResult = RuleTypes.TIME_OF_USE;
        scheduleResult = type;
    }
    return { priceResult, scheduleResult };
  };

  const { priceResult, scheduleResult } = getScheduleType(price?.scheduleType || RuleTypes.FREE);

  const [scheduleType, setScheduleType] = useState(scheduleResult || ScheduleType.PER_DAY);
  const [priceType, setPriceType] = useState(priceResult || RuleTypes.FREE);

  const priceTiered = price?.tieredPriceConditions?.map((item) => {
    return {
      ...item,
      id: uniqueId(),
      price: Number(item.price?.substring(1)).toFixed(2),
    };
  });

  const [tierPrices, setTierPrices] = useState<TierPriceItem[]>(
    priceTiered?.length ? priceTiered : [tierPriceItem(), tierPriceItem()],
  );

  const [triggerCreatePricing, createPricingQueryResult] = useCreatePricingModelMutation();
  const [triggerEditPricing, editPricingQueryResult] = useEditPricingModelMutation();
  const [triggerCheckGuardrails] = useCheckChargerPriceRuleGuardrailsMutation();
  const [chargerGuardrails, setChargerGuardrails] = useState<Map<string, PriceRuleGuardrail[]>>(
    new Map(),
  );

  const { locations } = useFetchLocationsQuery(
    { scope: userScope!, include: 'all' },
    {
      selectFromResult: (endPoint) => ({
        locations: endPoint?.data ? endPoint.data.entities : [],
      }),
    },
  );

  const [idleFeeDetails, setIdleFeeDetails] = useState({
    enable: !!price?.idleRate,
    idleRate: price?.idleRate ? Number(price?.idleRate).toFixed(2) : '',
  });

  const [sessionTimeLimitDetails, setSessionTimeLimitDetails] = useState({
    enable: !!price?.sessionTimeLimitMinutes,
    sessionTimeLimitHours:
      price && typeof price.sessionTimeLimitMinutes === 'number'
        ? String(price.sessionTimeLimitMinutes / 60)
        : '',
  });

  const chargersDrodownList = useMemo(() => {
    return locations
      .map((location) => {
        const children = location.chargers
          ?.filter((charger: Charger) => {
            // we only display undefault chargers for default pricing rule.
            if (!isDefaultPricingRule) return true;
            return !defaultPricingRule?.chargers.some(
              (defaultCharger) => defaultCharger.chargerId === charger.id,
            );
          })
          .map((charger: Charger) => {
            return {
              id: charger.id,
              label: charger.displayName,
              selected: chargers.includes(charger.id),
            };
          });

        if (children?.length === 0) {
          return null;
        }
        return {
          id: location.id,
          label: location.name,
          children: children || [],
          selected: children?.length ? children?.every((child) => child.selected) : false,
        };
      })
      .filter((location) => location);
  }, [chargers, locations, isDefaultPricingRule, defaultPricingRule]);

  const chargerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((location) => {
      location.chargers?.forEach((charger: Charger) => {
        map.set(charger.id, charger.displayName);
      });
    });
    return map;
  }, [locations]);

  useEffect(() => {
    if (chargers.length === 0) {
      setChargerGuardrails(new Map());
      return;
    }

    const fetchAll = async () => {
      try {
        const result = await triggerCheckGuardrails({ deviceIds: chargers }).unwrap();
        const updated = new Map<string, PriceRuleGuardrail[]>();
        Object.entries(result.guardrails).forEach(([id, guardrails]) => {
          if (guardrails.length > 0) {
            updated.set(id, guardrails);
          }
        });
        setChargerGuardrails(updated);
      } catch (e) {
        logger.error('checkChargerPriceRuleGuardrails failed', e as Error);
      }
    };

    fetchAll();
  }, [chargers, triggerCheckGuardrails]);

  const activePricingRules = useMemo(() => {
    const active = new Set<string>();

    if (priceType === RuleTypes.TIERED) {
      active.add('TIERED');
      tierPrices.forEach((tier: TierPriceItem) => {
        if (tier.pricingType) active.add(String(tier.pricingType));
      });
    }

    if (priceType === RuleTypes.TIME_OF_USE) {
      dayPrices?.forEach((day) => {
        day.pricePeriods?.forEach((period) => {
          if (period.pricingType) active.add(period.pricingType);
        });
      });
    }

    if (idleFeeDetails.enable && Number(idleFeeDetails.idleRate) > 0) {
      active.add('IDLE_RATE');
    }

    return active;
  }, [priceType, dayPrices, tierPrices, idleFeeDetails]);

  useEffect(() => {
    const guardrailChargers = new Map<string, { message: string; chargerNames: string[] }>();
    const lang = i18n.language.split('-')[0];

    chargerGuardrails.forEach((guardrails, chargerId) => {
      const chargerName = chargerNameMap.get(chargerId) || chargerId;
      guardrails.forEach((guardrail) => {
        const violated = guardrail.restrictedPricingRules.some((rule) =>
          activePricingRules.has(rule),
        );
        if (!violated) return;

        const guardrailMessage = guardrail.message;

        const msg = guardrailMessage[lang] || guardrailMessage.en;

        const existing = guardrailChargers.get(guardrail.id);
        if (existing) {
          existing.chargerNames.push(chargerName);
        } else {
          guardrailChargers.set(guardrail.id, { message: msg, chargerNames: [chargerName] });
        }
      });
    });

    const messages: string[] = [];
    guardrailChargers.forEach(({ message, chargerNames }) => {
      messages.push(`${chargerNames.join(', ')}: ${message}`);
    });
    setGuardrailAlertMessages(messages);
  }, [chargerGuardrails, chargerNameMap, activePricingRules, t]);

  const scheduleItems = useMemo(() => {
    return Object.keys(ScheduleType).map((key) => {
      return {
        id: key,
        label: getScheduleLabel(key),
        selected: key === scheduleType,
        disabled: editPricingQueryResult.isLoading || createPricingQueryResult.isLoading,
      };
    });
  }, [scheduleType, editPricingQueryResult.isLoading, createPricingQueryResult.isLoading]);

  const handleCalendarChange = (data: DayPrice[]) => {
    setDayPrices(data);
  };

  const handleScheduleTypeChange = (items: CheckBoxData[]) => {
    items.forEach((item) => {
      if (item && item.selected && item?.id) {
        setScheduleType(item.id as ScheduleType);
      }
    });
  };

  const handleChargersChange = (items: DropdownOption[]) => {
    const newChargers: string[] = [];
    items.forEach((item: DropdownOption) => {
      item.children.forEach((child: { id: string; selected: boolean }) => {
        if (child.selected) {
          newChargers.push(child.id);
        }
      });
    });
    setChargers(newChargers);
  };

  const idleFeeStatus = useMemo(() => {
    if (!idleFeeDetails.enable || !idleFeeDetails.idleRate) {
      return IdleFeeStatus.EMPTY;
    }

    const rate = Number(idleFeeDetails.idleRate);
    if (Number.isNaN(rate)) {
      return IdleFeeStatus.EMPTY;
    }

    const currencyRanges = CURRENCY_IDLE_FEE_RANGES[company?.currency as Currency];
    if (!currencyRanges) {
      return IdleFeeStatus.EXCEED_LIMIT;
    }

    if (rate <= currencyRanges.tooLowAmount) {
      return IdleFeeStatus.TOO_LOW;
    }
    if (rate >= currencyRanges.endAmount + 0.01 && rate <= currencyRanges.maxAmount) {
      return IdleFeeStatus.TOO_HIGH;
    }
    if (rate > currencyRanges.maxAmount) {
      return IdleFeeStatus.EXCEED_LIMIT;
    }

    return IdleFeeStatus.APPROPRIATE;
  }, [idleFeeDetails, company?.currency]);

  const onValidation = () => {
    if (_.isEmpty(_.trim(name))) {
      return false;
    }
    // Disabling save button until user enters the duration & price for tiered pricing rule
    if (priceType === RuleTypes.TIERED) {
      const hasInvalidValue = tierPrices?.some((item, index) => {
        // check price
        if (_.isEmpty(item.price) || !validateCurrency(item.price, false)) {
          return true;
        }
        // check duration
        if (
          index !== tierPrices.length - 1 &&
          (_.isEmpty(item.duration) || Number(item.duration) === 0)
        ) {
          return true;
        }
        return false;
      });
      if (hasInvalidValue) {
        return false;
      }
    }

    // disable when idle pricing enable
    if (
      idleFeeDetails.enable &&
      (idleFeeStatus === IdleFeeStatus.EMPTY || idleFeeStatus === IdleFeeStatus.EXCEED_LIMIT)
    ) {
      return false;
    }

    return true;
  };

  const schedule = () => {
    let result;
    if (priceType === RuleTypes.TIME_OF_USE) {
      result = scheduleType;
    } else {
      result = priceType;
    }
    return result;
  };

  // tieredPrices modify request data / updates start boundary

  const tieredPrices = tierPrices.map((item) => {
    const response = {
      ...item,
      price: `$${item.price}`,
    };

    delete response.id;
    return response;
  });

  const savePricing = (data: GenericPricingPayload) => {
    const request = { ...data };
    if (!chargers?.length) {
      request.chargerIds = [];
    }
    if (priceType !== RuleTypes.TIERED) {
      request.tieredPriceConditions = [];
    } else {
      request.dayPrices = [];
    }
    if (price?.id) {
      captureAnalyticsEvent(AnalyticsEvent.PRICING_EDIT_EVENT);
      triggerEditPricing(request as EditPricingPayload);
    } else {
      triggerCreatePricing(request);
    }
  };

  const handleIdleFeeDetails = (details: IdleFeeDetails) => {
    setIdleFeeDetails({
      ...details,
      idleRate: details.idleRate.toString(),
    });
  };

  const handleSessionTimeLimitDetails = (details: SessionTimeLimitDetails) => {
    setSessionTimeLimitDetails({
      ...details,
      sessionTimeLimitHours: details.sessionTimeLimitHours.toString(),
    });
  };

  const handleSave = () => {
    // Format these info for server need
    const dayPriceWithDollar = dayPrices?.map((dayPrice) => {
      const day = capitalizeFirstLetter(dayPrice.day);
      return {
        day,
        pricePeriods: dayPrice.pricePeriods.map((pp) => ({
          ...pp,
          price: pp.price,
        })),
      };
    });

    const pricingPayload: EditPricingPayload = {
      id: price?.id || '',
      displayName: name,
      // for default pricing rule, we should concat the existing chargers and new selected chargers.
      chargerIds:
        (isDefaultPricingRule
          ? defaultPricingRule?.chargers.map((charger) => charger.chargerId).concat(chargers)
          : chargers) || [],
      scheduleType: schedule(),
      tieredPriceConditions: tieredPrices,
      dayPrices: dayPriceWithDollar,
      idleRate: idleFeeDetails.enable ? Number(idleFeeDetails.idleRate) : undefined,
    };
    if (sessionTimeLimitDetails.enable) {
      pricingPayload.sessionTimeLimitMinutes =
        Number(sessionTimeLimitDetails.sessionTimeLimitHours) * 60;
    }
    savePricing(pricingPayload);
  };

  const handleLeave = () => {
    navigateWithCompanyId(`../${RoutePath.PRICING}`, companyId, {
      replace: true,
    });
  };

  const handleCancel = () => {
    showModal(MODAL_TYPES.ALERT_MODAL, {
      height: 'max-content',
      title: t('pricing_unsaved_chargers'),
      message: t('pricing_unsaved_message'),
      buttons: [
        {
          label: t('pricing_go_back'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
        },
        {
          label: t('pricing_leave_without_saving'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
          onClick: handleLeave,
        },
      ],
    });
  };

  const renderChargersOverlapWarn = () => {
    if (isDefaultPricingRule && chargers.length > 0) {
      return (
        <div className='mt-[18px]'>
          <Label
            icon={Info}
            text={t('edit_default_pricing_rule_warn')}
            type={LabelType.BODY3}
            color={ColorType.GREY6}
          />
        </div>
      );
    }
    if (!isChargerFromDefaultRule(defaultPricingRule, chargers, price)) {
      return (
        <div className='mt-[18px]'>
          <Label
            icon={Info}
            text={t('pricing_chargers_overlap_warn')}
            type={LabelType.BODY3}
            color={ColorType.GREY6}
          />
        </div>
      );
    }
    return null;
  };

  const handleAddCharger = () => {
    setShowDefaultChargerDropdown(true);
  };

  const renderAddChargersButton = () => {
    if (!isDefaultPricingRule || (isDefaultPricingRule && showDefaultChargeDropdown)) {
      return null;
    }
    return (
      <div>
        <Label
          text={t('chargers_add_label')}
          svgIcon={AddPlus2}
          svgColor={theme.navigationSelectedColor}
          type={LabelType.LABEL_S}
          color={theme.navigationSelectedColor as ColorType}
          onClick={handleAddCharger}
        />
      </div>
    );
  };

  const renderChargersDropdown = () => {
    if (isDefaultPricingRule && !showDefaultChargeDropdown) {
      return null;
    }
    return (
      <Dropdown
        placeholder={t('pricing_chargers_holder')}
        placeholderLabelType={LabelType.DROPDOWN_HEADER}
        headerWidth={450}
        items={chargersDrodownList}
        type={DropdownType.CHECKBOX_TREE}
        showPillHeader
        onItemClick={(items: DropdownOption[]) => handleChargersChange(items)}
        showFooter
        dataTestId='selectChargers'
        iconAlt={t('icon_alt_open_chargers_dropdown')}
        pillCloseIconAlt={t('icon_alt_remove_charger')}
      />
    );
  };

  const renderDefaultChargersInfo = () => {
    if (!isDefaultPricingRule) {
      return null;
    }
    return (
      <div className='flex flex-row items-center mt-[16px]'>
        <Label
          className='w-[128px]'
          text={t('pricing_chargers')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
        />
        <div className='inline-flex flex-wrap gap-1 overflow-x-hidden w-[900px]'>
          {defaultPricingRule?.chargers.map((charger) => {
            return <Pill key={charger.chargerId} label={charger.chargerName} />;
          })}
        </div>
      </div>
    );
  };

  const renderChargersDropdownInfo = () => {
    if (defaultPricingRule === undefined) {
      return null;
    }
    return (
      <div className='flex flex-row items-center mt-[16px]'>
        <Label
          className='w-[128px]'
          text={
            isDefaultPricingRule
              ? showDefaultChargeDropdown
                ? t('pricing_rule_chargers_to_add')
                : ''
              : t('pricing_chargers')
          }
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
        />
        <div className='flex flex-col'>
          {renderAddChargersButton()}
          {renderChargersDropdown()}
        </div>
      </div>
    );
  };

  const renderGuardrailAlerts = () => {
    const visible = guardrailAlertMessages.filter((msg) => !dismissedGuardrails.has(msg));
    if (visible.length === 0) {
      return null;
    }
    return (
      <div className='flex flex-col gap-2 mt-4'>
        {visible.map((message) => (
          <div
            key={message}
            className='w-fit flex flex-row gap-2 p-2 bg-warning0 border-2 border-warning rounded-lg items-center'
          >
            <Icon color={ColorType.WARNING} src={ErrorWarningtraiangle} className='shrink-0' />
            <Label text={message} type={LabelType.BODY3} color={ColorType.GREY6} />
            <button
              type='button'
              className='ml-1 shrink-0 text-grey5 hover:text-grey6 text-lg leading-none cursor-pointer'
              onClick={() => setDismissedGuardrails((prev) => new Set(prev).add(message))}
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderPricingSchedule = () => {
    return (
      <Form
        onSubmit={handleSave}
        queryResult={price?.id ? editPricingQueryResult : createPricingQueryResult}
        onQuerySuccess={() => {
          navigateWithCompanyId(`../${RoutePath.PRICING}`, companyId, {
            replace: true,
          });
        }}
        onQueryFailed={() => {
          navigateWithCompanyId(`../${RoutePath.PRICING}`, companyId, {
            replace: true,
          });
        }}
      >
        <div className='flex flex-col gap-[18px] mt-4'>
          <div className='flex flex-col'>
            <div className='flex flex-row h-[40px] items-center'>
              <Label
                className='w-[128px]'
                text={t('pricing_rule_name')}
                type={LabelType.LABEL_S}
                color={ColorType.GREY6}
                labelHtmlFor='pricing_rule_name_holder'
              />
              <FormInput
                inputId='pricing_rule_name_holder'
                placeholder={t('pricing_rule_name_holder')}
                onChange={(event) => setName(event.target.value)}
                defaultValue={name.trimStart()}
                width='335px'
                disabled={isDefaultPricingRule}
                dataTestId='pricingRuleName'
              />
            </div>
            {renderDefaultChargersInfo()}
            {renderChargersDropdownInfo()}

            {renderChargersOverlapWarn()}
            {renderGuardrailAlerts()}
            <PriceForm
              priceType={priceType}
              setPriceType={setPriceType}
              scheduleItems={scheduleItems}
              scheduleType={scheduleType}
              dayPrices={price?.dayPrices}
              tierPrices={tierPrices}
              idleFeeDetails={idleFeeDetails}
              setIdleFeeDetails={handleIdleFeeDetails}
              sessionTimeLimitDetails={sessionTimeLimitDetails}
              setSessionTimeLimitDetails={handleSessionTimeLimitDetails}
              setTierPrices={setTierPrices}
              handleCalendarChange={handleCalendarChange}
              handleScheduleTypeChange={handleScheduleTypeChange}
              disabled={editPricingQueryResult.isLoading || createPricingQueryResult.isLoading}
              companyInfo={company}
              idleFeeStatus={idleFeeStatus}
              isSessionLimitEnabled={isSessionLimitEnabled}
            />
          </div>
          <div className='flex flex-row-reverse mt-[20px] gap-2'>
            <Button
              label={t('save')}
              size={ButtonSize.SMALL}
              type={ButtonType.PRIMARY}
              disabled={!onValidation()}
              isSumbit
              dataTestId='pricingRuleSave'
            />
            <Button
              label={t('cancel')}
              size={ButtonSize.SMALL}
              type={ButtonType.TERTIARY}
              onClick={handleCancel}
            />
          </div>
        </div>
      </Form>
    );
  };
  return (
    <div>
      <Card title={price ? t('pricing_edit_rule') : t('pricing_new_rule')}>
        {/* Session Fee Alert */}
        {sessionFee > 0 && (
          <div className='w-fit'>
            <div className='flex flex-row gap-2 p-2 bg-warning0 border-2 border-warning rounded-lg items-center'>
              <Icon color={ColorType.WARNING} src={ErrorWarningtraiangle} />
              <Label
                htmlText={t('session_fee_alert_message', {
                  sessionFee: sessionFee.toFixed(2),
                })}
                type={LabelType.BODY3}
                color={ColorType.GREY6}
              />
            </div>
          </div>
        )}
        {renderPricingSchedule()}
      </Card>
    </div>
  );
};
